import ReceivedAtDate from '../../../../../../components/ReceivedAtDate';
import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import { actions, PLOT_DATA_RESULT } from '../actions';
import Strings from '../../../../../../Strings';
import { PageHeader } from '../../../../../../components/PageHeader';
import { formatTimeSpanHHMM } from '../../../../../../utils';
import SleepGraphHourly from '../../../../../../components/Graphs/SleepGraphHourly';
import { DATE_FORMAT_YEAR_MONTH_DAY, DATE_MONTH_DAY_YEAR, TIME_FORMAT_12_LOWERCASE, PAGE_LIMIT } from '../../../../../../constants';
import Table3, { columnHelper } from '../../../../../../containers/Table/Table3';
import SleepGraph from '../../../../../../components/Graphs/SleepGraph';

export const SLEEP_DEPTH_UNKNOWN = 'unknown';

export const prepareSleepData = rawData => {
  const sorted = rawData.sort((a, b) => a.sleep_start - b.sleep_start);
  const ret = sorted.map((sleep, i) => {
    const sleepStart = moment(sleep.sleep_start * 1000);
    const sleepEnd = moment(sleep.sleep_stop * 1000);
    const sleepStartHour =
      sleepStart.hours() >= 0
        ? sleepStart.hours() + sleepStart.minutes() / 60
        : -24 + sleepStart.hours() + sleepStart.minutes() / 60;
    const sleepEndHour = sleepEnd.hours() + sleepEnd.minutes() / 60;
    // A manually entered sleep has no stages: the whole session is one part of unknown depth
    const sleepParts = sleep.sleep_parts?.length
      ? sleep.sleep_parts.sort((a, b) => a.sleep_start - b.sleep_start)
      : [{ sleep_start: sleep.sleep_start, sleep_stop: sleep.sleep_stop, sleep_depth: SLEEP_DEPTH_UNKNOWN }];
    const duration = (sleep.sleep_stop - sleep.sleep_start) / 60;
    const completedTimestamp = Math.max(sleep.sleep_stop || 0, ...sleepParts.map(part => part.sleep_stop || 0));
    const completedAt = completedTimestamp > 0 ? moment.unix(completedTimestamp) : null;
    let change = 0;
    if (i > 0) {
      const prevDuration = (sorted[i - 1].sleep_stop - sorted[i - 1].sleep_start) / 60;
      change = ((duration - prevDuration) / prevDuration) * 100;
    }
    return {
      sleepStart,
      sleepEnd,
      completedAt,
      backend_timestamp: sleep.backend_timestamp,
      duration,
      change,
      awakeDuration: sleep.awake_duration,
      remDuration: sleep.rem_duration,
      lightSleepDuration: sleep.light_sleep_duration,
      deepSleepDuration: sleep.deep_sleep_duration,
      sleepStartHour,
      sleepEndHour,
      date: moment(sleep.sleep_stop * 1000).startOf('day'),
      x: moment(sleep.sleep_stop * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY),
      parts: sleepParts.map(part => {
        const partStart = moment(part.sleep_start * 1000);
        const partEnd = moment(part.sleep_stop * 1000);
        return {
          sleepStart: partStart,
          completedAt,
          backend_timestamp: sleep.backend_timestamp,
          sleepEnd: partEnd,
          depth: part.sleep_depth,
          duration: partEnd.diff(partStart) / 1000 / 60,
          duration2: part.sleep_duration,
        };
      }),
    };
  });

  return ret;
};

export function SleepLegend(props) {
  const { graphType, onMouseLeave, onMouseOver } = props;
  // Stage times come only from sleeps a device reported, not from manually entered ones
  const data = props.data?.filter(e => Number.isFinite(e.awakeDuration));
  let awake, rem, lightSleep, deepSleep;

  if (data && data.length > 0) {
    if (graphType === 'daily') {
      const getAverage = (array, fn = v => v) => {
        var reducer = fn => (total, item) => total + fn(item),
          sum = array.reduce(reducer(fn), 0);
        return sum / array.length;
      };

      awake = `${formatTimeSpanHHMM(getAverage(data, e => e.awakeDuration))}`;
      rem = `${formatTimeSpanHHMM(getAverage(data, e => e.remDuration))}`;
      lightSleep = `${formatTimeSpanHHMM(getAverage(data, e => e.lightSleepDuration))}`;
      deepSleep = `${formatTimeSpanHHMM(getAverage(data, e => e.deepSleepDuration))}`;
    } else {
      const getSum = (array, fn = v => v) => {
        var reducer = fn => (total, item) => total + fn(item),
          sum = array.reduce(reducer(fn), 0);
        return sum;
      };

      awake = formatTimeSpanHHMM(getSum(data, e => e.awakeDuration) / data.length);
      rem = formatTimeSpanHHMM(getSum(data, e => e.remDuration));
      lightSleep = formatTimeSpanHHMM(getSum(data, e => e.lightSleepDuration) / data.length);
      deepSleep = formatTimeSpanHHMM(getSum(data, e => e.deepSleepDuration) / data.length);
    }
  }

  return (
    <div className="legendContainer">
      <div className="dot awake"></div>
      <div className="label" id="legend_awake" onMouseOver={() => onMouseOver('awake')} onMouseLeave={onMouseLeave}>
        {`${Strings.awake}${awake ? ` - ${awake}` : ''}`}
        {graphType === 'daily' && <div className="greyLabel">&ensp;(avg)</div>}
      </div>
      <div className="dot rem"></div>
      <div className="label" id="legend_rem" onMouseOver={() => onMouseOver('rem')} onMouseLeave={onMouseLeave}>
        {`${Strings.rem}${rem ? ` - ${rem}` : ''}`}
        {graphType === 'daily' && <div className="greyLabel">&ensp;(avg)</div>}
      </div>
      <div className="dot lightSleep"></div>
      <div
        className="label"
        id="legend_lightSleep"
        onMouseOver={() => onMouseOver('light')}
        onMouseLeave={onMouseLeave}
      >
        {`${Strings.lightSleep}${lightSleep ? ` - ${lightSleep}` : ''}`}
        {graphType === 'daily' && <div className="greyLabel">&ensp;(avg)</div>}
      </div>
      <div className="dot deepSleep"></div>
      <div className="label" id="legend_deepSleep" onMouseOver={() => onMouseOver('deep')} onMouseLeave={onMouseLeave}>
        {`${Strings.deepSleep}${deepSleep ? ` - ${deepSleep}` : ''}`}
        {graphType === 'daily' && <div className="greyLabel">&ensp;(avg)</div>}
      </div>
    </div>
  );
}

function SleepReport(props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedPhase, setSelectedPhase] = React.useState(undefined);
  const [data, setData] = React.useState([]);
  const [dataType] = React.useState('Monthly');
  const [pagination, setPagination] = React.useState({ offset: 0, total: 0, limit: PAGE_LIMIT });
  const pageRequestRef = React.useRef({ offset: 0, limit: PAGE_LIMIT });

  const getPlotData = React.useCallback(() => {
    setIsLoading(true);

    const { deviceId, startDate, endDate, hubId, kitId, patientId } = props;
    if (deviceId && deviceId !== '' && startDate && startDate !== '' && endDate && endDate !== '') {
      const request = {
        deviceId,
        dataType: 'sleep_report',
        startDate,
        endDate,
        hubId,
        kitId,
        patientId,
        limit: pageRequestRef.current.limit,
        offset: pageRequestRef.current.offset,
      };

      props.getPlotData(request).then(resp => {
        let newData = [];
        if (resp.type === PLOT_DATA_RESULT) {
          newData = prepareSleepData(resp.response.data);
          setPagination({
            offset: pageRequestRef.current.offset,
            total: resp.response.pagination?.total || newData.length,
            limit: pageRequestRef.current.limit,
          });
        }
        setIsLoading(false);
        setData(newData);
      });
    }
  }, [props.deviceId, props.startDate, props.endDate, props.hubId, props.kitId, props.patientId]);

  React.useEffect(() => {
    pageRequestRef.current.offset = 0;
    getPlotData();
  }, [props.deviceId, props.startDate, props.endDate]);

  const onPaginationChange = React.useCallback((newPagination) => {
    pageRequestRef.current.offset = newPagination.pageIndex * pageRequestRef.current.limit;
    getPlotData();
  }, [getPlotData]);

  const onPageSizeChange = React.useCallback((newSize) => {
    pageRequestRef.current.offset = 0;
    pageRequestRef.current.limit = newSize;
    getPlotData();
  }, [getPlotData]);

  const onMouseOver = (e) => {
    setSelectedPhase(e);
  };

  const onMouseLeave = () => {
    setSelectedPhase(undefined);
  };

  const formatPercentChange = (value) => {
    return `${value < 0 ? '' : '+'}${value.toFixed(1)}%`;
  };

  const getStringFromPhase = (phase) => {
    switch (phase) {
      case 'rem':
        return Strings.rem;
      case 'deep_sleep':
        return Strings.deepSleep;
      case 'light_sleep':
        return Strings.lightSleep;
      case 'awake':
        return Strings.awake;
      default:
        return '-';
    }
  };

  const monthlyColumns = React.useMemo(() => [
    columnHelper.accessor('date', {
      header: Strings.date,
      cell: info => (
        <ReceivedAtDate displayReference={info.getValue()} displayFormat={`${DATE_MONTH_DAY_YEAR} ${TIME_FORMAT_12_LOWERCASE}`} measuredAt={info.row.original.completedAt} backendTimestamp={info.row.original.backend_timestamp}>
          {info.getValue()?.format(DATE_MONTH_DAY_YEAR)}
        </ReceivedAtDate>
      ),
      size: 120,
    }),
    columnHelper.accessor('duration', {
      header: Strings.opportunity,
      cell: info => formatTimeSpanHHMM(info.getValue()),
      size: 100,
    }),
    columnHelper.accessor('change', {
      header: Strings.percentChange,
      cell: info => formatPercentChange(info.getValue()),
      size: 120,
    }),
    columnHelper.accessor('awakeDuration', {
      header: Strings.awake,
      cell: info => formatTimeSpanHHMM(info.getValue()),
      size: 100,
    }),
    columnHelper.accessor('remDuration', {
      header: Strings.rem,
      cell: info => formatTimeSpanHHMM(info.getValue()),
      size: 100,
    }),
    columnHelper.accessor('lightSleepDuration', {
      header: Strings.lightSleep,
      cell: info => formatTimeSpanHHMM(info.getValue()),
      size: 100,
    }),
    columnHelper.accessor('deepSleepDuration', {
      header: Strings.deepSleep,
      cell: info => formatTimeSpanHHMM(info.getValue()),
      size: 100,
    }),
  ], []);

  const dailyColumns = React.useMemo(() => [
    columnHelper.accessor('sleepStart', {
      header: Strings.time,
      cell: info => (
        <ReceivedAtDate displayReference={info.getValue()} displayFormat={`${DATE_MONTH_DAY_YEAR} ${TIME_FORMAT_12_LOWERCASE}`} measuredAt={info.row.original.completedAt} backendTimestamp={info.row.original.backend_timestamp}>
          {info.getValue().format(TIME_FORMAT_12_LOWERCASE)}
        </ReceivedAtDate>
      ),
      size: 100,
    }),
    columnHelper.accessor('sleepEnd', {
      header: Strings.ended,
      cell: info => info.getValue().format(TIME_FORMAT_12_LOWERCASE),
      size: 100,
    }),
    columnHelper.accessor('duration', {
      header: Strings.length,
      cell: info => `${info.getValue()} ${Strings.unitsEnum.minutes}`,
      size: 100,
    }),
    columnHelper.accessor('depth', {
      header: Strings.state,
      cell: info => getStringFromPhase(info.getValue()),
      size: 100,
    }),
  ], []);

  const getTable = () => {
    if (dataType === 'Monthly') {
      return (
        <Table3
          name="sleep-report-monthly"
          isLoading={isLoading}
          data={data && data.length > 0 ? data.slice().reverse() : []}
          columns={monthlyColumns}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          onPageSizeChange={onPageSizeChange}
        />
      );
    } else if (dataType === 'Daily') {
      return (
        <Table3
          name="sleep-report-daily"
          isLoading={isLoading}
          data={data && data.length > 0 ? data[0].parts.slice().reverse() : []}
          columns={dailyColumns}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          onPageSizeChange={onPageSizeChange}
        />
      );
    }
    return <React.Fragment />;
  };

  const getPageHeader = () => {
    return (
      <PageHeader
        left={
          <SleepLegend
            data={data}
            dataType={dataType}
            onMouseOver={onMouseOver}
            onMouseLeave={onMouseLeave}
          />
        }
      />
    );
  };

  const getGraph = () => {
    const { startDate, endDate } = props;
    if (data && data.length > 0)
      return (
        <div className="hub-device-events-margin-bottom">
          {dataType === 'Monthly' ? (
            <SleepGraph
              graphData={data}
              minDate={moment(startDate)}
              maxDate={moment(endDate)}
              selectedPhase={selectedPhase}
              hasBorder
            />
          ) : (
            <SleepGraphHourly graphData={data} date={moment(startDate)} selectedPhase={selectedPhase} />
          )}
        </div>
      );
    return (
      <div className="empty-row-container bottom-margin">
        <div className="empty-row-inner">{Strings.noDataAvailable}</div>
      </div>
    );
  };

  return (
    <React.Fragment>
      {getPageHeader()}
      {getGraph()}
      {getTable()}
    </React.Fragment>
  );
}

SleepReport.propTypes = {
  deviceId: PropTypes.string.isRequired,
  patientId: PropTypes.string,
  hubId: PropTypes.string,
  kitId: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  getPlotData: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getPlotData: request => dispatch(actions.getPlotData(request)),
});

export default connect(null, mapDispatchToProps)(SleepReport);
